import { Box, Grid, Typography } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import Link from "@mui/material/Link";
import { useTheme } from "@mui/material/styles";
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { generateDeviceFingerprint } from '../utils/deviceFingerprint';

const LOGIN_RED = "#C41E26";
const LOGIN_RED_DEEP = "#9E151C";
const HEADLINE_MUTED = "#F5C4C6";

const loginStats = [
  { value: "190K+", label: "Vehicle types cross-referenced" },
  { value: "9.8M", label: "Parts articles in the catalog" },
  { value: "1,000+", label: "Brand integrations" },
  { value: "Unlimited", label: "Able to create unlimited users" },
];

const Login = () => {
  const baseURL = process.env.REACT_APP_API_URL;
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const navigate = useNavigate();

  const validateForm = React.useCallback(() => {
    if (username.length === 0 || password.length === 0) {
      setError("");
      setWarning("Username and password are required");
      return false;
    }
    setWarning("");
    return true;
  }, [username, password]);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError("");
    setWarning("");
  
    try {
      // Generate device fingerprint
      const deviceData = await generateDeviceFingerprint();
      
      // Create form data for login
      const formDetails = new URLSearchParams();
      formDetails.append("username", username);
      formDetails.append("password", password);
      
      // Add device data as JSON string
      formDetails.append("device_data", JSON.stringify(deviceData));
  
      const response = await fetch(`${baseURL}/auth/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formDetails,
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.access_token);
        localStorage.setItem("user", JSON.stringify(data.user));

        // Always land at the top of the destination page after login
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        if (data.user.is_admin) {
          navigate("/admin/dashboard", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.detail || "Authentication failed!";
        
        // Check if it's a device restriction error
        if (response.status === 403 && errorMessage.includes("Device not recognized")) {
          setWarning(
            "⚠️ Device Access Restricted\n\n" +
            "You are trying to login from a different device than the one you registered with. " +
            "For security reasons, you can only login from the same device you used during your first login.\n\n" +
            "If you need to access your account from a different device, please contact your administrator."
          );
        } else {
          setError(errorMessage);
        }
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      setError("An error occurred, Please try again later.");
    }
  };

  return (
    <>
    <Grid container spacing={0}>
      {!isTablet && (
        <Grid
          item
          xs={0}
          sm={0}
          md={6}
          lg={8}
          sx={{
            position: "relative",
            minHeight: "100vh",
            overflow: "hidden",
            background: `radial-gradient(ellipse 90% 70% at 25% 20%, ${LOGIN_RED} 0%, ${LOGIN_RED_DEEP} 70%, #7A1015 100%)`,
            color: "#fff",
          }}
        >
          {/* AP watermark */}
          <Typography
            aria-hidden
            sx={{
              position: "absolute",
              right: { md: "-4%", lg: "-2%" },
              top: "42%",
              transform: "translateY(-50%)",
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 700,
              fontSize: { md: "22rem", lg: "28rem" },
              lineHeight: 0.8,
              letterSpacing: "-0.04em",
              color: "rgba(0,0,0,0.12)",
              userSelect: "none",
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            AP
          </Typography>

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "100vh",
              px: { md: 5, lg: 8 },
              py: { md: 5, lg: 6 },
              boxSizing: "border-box",
            }}
          >
            {/* Brand lockup */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                animation: "fadeIn 0.7s ease-out both",
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "10px",
                  bgcolor: "#fff",
                  color: LOGIN_RED,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontWeight: 700,
                  fontSize: "1.45rem",
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                A
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: '"Playfair Display", Georgia, serif',
                    fontWeight: 700,
                    fontSize: "1.35rem",
                    lineHeight: 1.15,
                    color: "#fff",
                  }}
                >
                  AutoPasion
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "0.65rem",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.88)",
                    mt: 0.25,
                  }}
                >
                  Auto Parts Platform
                </Typography>
              </Box>
            </Box>

            {/* Headline + subcopy */}
            <Box
              sx={{
                maxWidth: 560,
                animation: "fadeIn 0.85s ease-out 0.12s both",
              }}
            >
              <Typography
                component="h1"
                sx={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontWeight: 700,
                  fontSize: { md: "3.4rem", lg: "4.25rem" },
                  lineHeight: 1.08,
                  letterSpacing: "-0.02em",
                  mb: 2.5,
                }}
              >
                <Box component="span" sx={{ display: "block", color: "#fff" }}>
                  Every Part.
                </Box>
                <Box component="span" sx={{ display: "block", color: "#fff" }}>
                  Every Vehicle.
                </Box>
                <Box
                  component="span"
                  sx={{ display: "block", color: HEADLINE_MUTED }}
                >
                  One Platform.
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  fontSize: { md: "1rem", lg: "1.1rem" },
                  lineHeight: 1.55,
                  color: "rgba(255,255,255,0.92)",
                  maxWidth: 420,
                }}
              >
                The professional-grade parts sourcing platform built for
                workshops, dealers, and distributors.
              </Typography>
            </Box>

            {/* Stats glass card */}
            <Box
              sx={{
                borderRadius: "18px",
                bgcolor: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.22)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                overflow: "hidden",
                animation: "fadeIn 0.9s ease-out 0.22s both",
                maxWidth: 720,
              }}
            >
              <Grid container>
                {loginStats.map((stat, index) => (
                  <Grid
                    key={stat.label}
                    item
                    xs={6}
                    sx={{
                      px: { md: 2.5, lg: 3.5 },
                      py: { md: 2.25, lg: 2.75 },
                      borderRight:
                        index % 2 === 0
                          ? "1px solid rgba(255,255,255,0.18)"
                          : "none",
                      borderBottom:
                        index < 2
                          ? "1px solid rgba(255,255,255,0.18)"
                          : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 700,
                        fontSize: { md: "1.75rem", lg: "2.1rem" },
                        lineHeight: 1.15,
                        color: "#fff",
                        mb: 0.5,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 400,
                        fontSize: { md: "0.8rem", lg: "0.88rem" },
                        lineHeight: 1.35,
                        color: "rgba(255,255,255,0.82)",
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Grid>
      )}

      <Grid item xs={12} sm={12} md={6} lg={4}>
        <div
          className="container"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <div className="login-form" style={{ width: "380px" }}>
            <img
              src="./logo.jpg" // Path relative to the public folder
              alt="Auto Kalluigjeri Logo"
              style={{
                display: "block",
                margin: "0 auto",
                width: "100%", // Adjust as needed
              }}
            />
            <div className="row" style={{marginTop: '20px'}}>
              <div className="col-md-6 offset-md-3">
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <input
                      required
                      type="text"
                      className="form-control"
                      id="username"
                      placeholder="Enter username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      style={{
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        padding: "10px",
                        margin: "5px 0px",
                        boxSizing: "border-box",
                        width: "100%",
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <input
                      required
                      type="password"
                      className="form-control"
                      id="password"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        padding: "10px",
                        margin: "5px 0px",
                        boxSizing: "border-box",
                        width: "100%",
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                    style={{
                      cursor: "pointer",
                      background: theme.palette.secondary.main,
                      color: theme.palette.primary.light,
                      borderRadius: "5px",
                      border: 0,
                      boxShadow: "none",
                      padding: "10px",
                      margin: "5px 0px",
                      boxSizing: "border-box",
                      width: "100%",
                    }}
                  >
                    {loading ? "Loading..." : "Login"}
                  </button>
                  <Stack sx={{ width: "100%", height: "48px" }} spacing={0}>
                    {error && (
                      <Alert
                        severity="error"
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 300,
                        }}
                      >
                        {error}
                      </Alert>
                    )}
                    {warning && (
                      <Alert
                        severity="warning"
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 300,
                        }}
                      >
                        {warning}
                      </Alert>
                    )}
                  </Stack>
                </form>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <span
                style={{
                  marginRight: "10px",
                  fontSize: "14px",
                  fontFamily: "Inter",
                }}
              >
                Powered by
              </span>
              <Link
                    href="https://www.tecmotion.co"
                    target="_blank"
                    rel="noopener"
                    sx={{ ml: 1, color: "#fff" }}
                  >
              <img
                src="./tecm_logo.png"
                alt="Tecm Logo"
                style={{
                  width: "150px",
                }}
              />
              </Link>
            </div>
          </div>
        </div>
      </Grid>
    </Grid>
    </>
  );
};

export default Login;
